import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/providers/auth-provider'
import { fetchAccountProfile } from '@/lib/api'

function dismissStorageKey(userId: string) {
  return `flownotes-password-prompt-dismissed-${userId}`
}

export function useNeedsPassword() {
  const { session, isAuthConfigured } = useAuth()
  const userId = session?.user?.id
  const [needsPassword, setNeedsPassword] = useState(false)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(
    async (options?: { ignoreDismiss?: boolean }) => {
      if (!isAuthConfigured || !userId) {
        setNeedsPassword(false)
        setLoading(false)
        return
      }

      if (!options?.ignoreDismiss && sessionStorage.getItem(dismissStorageKey(userId))) {
        setNeedsPassword(false)
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const data = await fetchAccountProfile()
        setNeedsPassword(Boolean(data.needsPassword))
      } catch {
        setNeedsPassword(false)
      } finally {
        setLoading(false)
      }
    },
    [isAuthConfigured, userId],
  )

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const onPasswordSet = () => {
      setNeedsPassword(false)
    }
    const onRecheck = () => {
      void refresh({ ignoreDismiss: true })
    }
    window.addEventListener('flownotes:password-set', onPasswordSet)
    window.addEventListener('flownotes:check-password-prompt', onRecheck)
    return () => {
      window.removeEventListener('flownotes:password-set', onPasswordSet)
      window.removeEventListener('flownotes:check-password-prompt', onRecheck)
    }
  }, [refresh])

  const dismiss = useCallback(() => {
    if (userId) {
      sessionStorage.setItem(dismissStorageKey(userId), '1')
    }
    setNeedsPassword(false)
  }, [userId])

  return { needsPassword, loading, dismiss, refresh }
}
